import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserCheck, faStar, faSearch } from '@fortawesome/free-solid-svg-icons';
import AlertModal from './AlertModal';

const AssignSupervisorModal = ({ quotaProjectId, cell, cells, pendingAssignments = [], pendingModifications = [], onClose, onSuccess }) => {
  // Selection form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [supervisors, setSupervisors] = useState([]);
  const [suggestedIds, setSuggestedIds] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Support both single cell and bulk mode
  const isBulk = Array.isArray(cells) && cells.length > 1;
  const currentCell = isBulk ? null : cell;
  const cellIds = isBulk ? cells.map(c => Number(c.id)) : (cell?.id ? [Number(cell.id)] : []);
  
  // Get pending assignments for this cell (prioritize pending over approved)
  const getInitialSelectedIds = (cellData, pendingAssignmentsData) => {
    if (!cellData?.id) return [];
    
    const cellId = Number(cellData.id);
    
    // First, check for pending assignments (highest priority)
    const cellPendingAssignments = pendingAssignmentsData.filter(pa => Number(pa.quota_cell_id) === cellId);
    if (cellPendingAssignments.length > 0) {
      return cellPendingAssignments.map(pa => Number(pa.supervisor_id)).filter(Boolean);
    }
    
    // If no pending assignments, check for approved supervisors
    if (cellData?.assigned_supervisors && Array.isArray(cellData.assigned_supervisors)) {
      return cellData.assigned_supervisors.map(s => Number(s.id)).filter(Boolean);
    }
    
    // Legacy single supervisor support
    if (cellData?.assigned_to_supervisor_id) {
      return [Number(cellData.assigned_to_supervisor_id)];
    }
    
    return [];
  };

  // Get existing assigned_count for a supervisor (only works for single cell mode)
  const getExistingAssignedCount = (supervisorId) => {
    if (isBulk || !currentCell?.id) return null;
    
    const cellId = Number(currentCell.id);
    
    // Check pending assignments first
    const cellPendingAssignments = pendingAssignments.filter(pa => 
      Number(pa.quota_cell_id) === cellId && Number(pa.supervisor_id) === supervisorId
    );
    if (cellPendingAssignments.length > 0 && cellPendingAssignments[0].assigned_count !== undefined && cellPendingAssignments[0].assigned_count !== null) {
      return Number(cellPendingAssignments[0].assigned_count);
    }
    
    // Check approved supervisors from assigned_supervisors array
    if (currentCell?.assigned_supervisors && Array.isArray(currentCell.assigned_supervisors)) {
      const supervisor = currentCell.assigned_supervisors.find(s => Number(s.id) === supervisorId);
      if (supervisor && supervisor.assigned_count !== undefined && supervisor.assigned_count !== null) {
        return Number(supervisor.assigned_count);
      }
    }
    
    // Try to get from supervisorAssignments relationship if available
    if (currentCell?.supervisorAssignments && Array.isArray(currentCell.supervisorAssignments)) {
      const assignment = currentCell.supervisorAssignments.find(a => 
        Number(a.supervisor_id) === supervisorId && 
        (a.assignment_approval_status === 'approved' || !a.assignment_approval_status)
      );
      if (assignment && assignment.assigned_count !== undefined && assignment.assigned_count !== null) {
        return Number(assignment.assigned_count);
      }
    }
    
    return null;
  };

  // Get existing completes for a supervisor (works for both single cell and bulk mode)
  const getExistingCompletes = (supervisorId) => {
    if (isBulk) {
      // For bulk mode, find the maximum completes across all selected cells
      let maxCompletes = 0;
      
      cells.forEach(cell => {
        const cellId = Number(cell.id);
        
        // Check pending assignments first
        const cellPendingAssignments = pendingAssignments.filter(pa => 
          Number(pa.quota_cell_id) === cellId && Number(pa.supervisor_id) === supervisorId
        );
        if (cellPendingAssignments.length > 0 && cellPendingAssignments[0].completes !== undefined) {
          const completes = Number(cellPendingAssignments[0].completes) || 0;
          maxCompletes = Math.max(maxCompletes, completes);
        }
        
        // Check approved supervisors
        if (cell?.assigned_supervisors && Array.isArray(cell.assigned_supervisors)) {
          const supervisor = cell.assigned_supervisors.find(s => Number(s.id) === supervisorId);
          if (supervisor?.completes !== undefined) {
            const completes = Number(supervisor.completes) || 0;
            maxCompletes = Math.max(maxCompletes, completes);
          }
        }
      });
      
      return maxCompletes;
    }
    
    // Single cell mode
    if (!currentCell?.id) return 0;
    
    const cellId = Number(currentCell.id);
    
    // Check pending assignments first
    const cellPendingAssignments = pendingAssignments.filter(pa => 
      Number(pa.quota_cell_id) === cellId && Number(pa.supervisor_id) === supervisorId
    );
    if (cellPendingAssignments.length > 0 && cellPendingAssignments[0].completes !== undefined) {
      return Number(cellPendingAssignments[0].completes) || 0;
    }
    
    // Check approved supervisors
    if (currentCell?.assigned_supervisors && Array.isArray(currentCell.assigned_supervisors)) {
      const supervisor = currentCell.assigned_supervisors.find(s => Number(s.id) === supervisorId);
      if (supervisor?.completes !== undefined) {
        return Number(supervisor.completes) || 0;
      }
    }
    
    return 0;
  };
  
  // Support both single supervisor (legacy) and multiple supervisors
  const [selectedSupervisorIds, setSelectedSupervisorIds] = useState(() => 
    isBulk ? [] : getInitialSelectedIds(currentCell, pendingAssignments)
  );
  const [distributeEvenly, setDistributeEvenly] = useState(true);
  const [assignments, setAssignments] = useState({}); // {supervisor_id: {assigned_count}}
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [alertModal, setAlertModal] = useState({ show: false, title: '', message: '', type: 'info' });
  const [isInitializing, setIsInitializing] = useState(true); // Track initialization to prevent race condition

  // Use pending target if a modification exists (chef_terrain may have changed target but not yet approved)
  const getEffectiveTarget = (cellData) => {
    if (!cellData?.id) return 0;
    const cellId = Number(cellData.id);
    const pendingTargetMod = pendingModifications.find(
      pm => Number(pm.quota_cell_id) === cellId && pm.field === 'target_count'
    );
    return pendingTargetMod ? pendingTargetMod.new_value : (cellData.target_count || 0);
  };

  const targetCount = isBulk
    ? (cells.reduce((sum, c) => sum + getEffectiveTarget(c), 0) / cells.length)
    : getEffectiveTarget(currentCell);
  const avgTargetCount = isBulk ? Math.floor(targetCount) : targetCount;

  useEffect(() => {
    if ((isBulk && cells.length > 0) || (!isBulk && currentCell?.id)) {
      setIsInitializing(true); // Set flag to prevent other effects from interfering
      
      // For bulk mode, check if all cells have the same supervisors assigned
      // For single cell mode, use existing logic
      let initialIds = [];
      if (isBulk) {
        // Aggregate supervisors from all cells (prioritize pending)
        const allSupervisorIds = new Set();
        cells.forEach(c => {
          const cellId = Number(c.id);
          const cellPendingAssignments = pendingAssignments.filter(pa => Number(pa.quota_cell_id) === cellId);
          if (cellPendingAssignments.length > 0) {
            cellPendingAssignments.forEach(pa => allSupervisorIds.add(Number(pa.supervisor_id)));
          } else if (c?.assigned_supervisors && Array.isArray(c.assigned_supervisors)) {
            c.assigned_supervisors.forEach(s => allSupervisorIds.add(Number(s.id)));
          }
        });
        initialIds = Array.from(allSupervisorIds);
      } else {
        initialIds = getInitialSelectedIds(currentCell, pendingAssignments);
      }
      setSelectedSupervisorIds(initialIds);
      
      // Initialize assignments from existing data (prioritize pending over approved)
      if (initialIds.length > 0) {
        const initialAssignments = {};
        let hasExistingAssignments = false;
        
        initialIds.forEach(supervisorId => {
          const existingCount = getExistingAssignedCount(supervisorId);
          console.log('AssignSupervisorModal - Initializing for supervisor:', supervisorId, {
            existingCount,
            cellId: currentCell?.id,
            assignedSupervisors: currentCell?.assigned_supervisors,
            supervisorAssignments: currentCell?.supervisorAssignments,
            pendingAssignments: pendingAssignments.filter(pa => Number(pa.quota_cell_id) === Number(currentCell?.id))
          });
          // If we found an existing count (even if 0), use it - this means there was a previous assignment
          if (existingCount !== null && existingCount !== undefined) {
            initialAssignments[supervisorId] = { assigned_count: existingCount };
            hasExistingAssignments = true;
          }
        });
        
        console.log('AssignSupervisorModal - Initial assignments:', initialAssignments, 'hasExistingAssignments:', hasExistingAssignments, 'distributeEvenly will be set to:', !hasExistingAssignments);
        
        if (hasExistingAssignments) {
          // If we have existing assignments, use custom distribution and prefill values
          setAssignments(initialAssignments);
          setDistributeEvenly(false);
        } else {
          // No existing assignments, calculate based on number of supervisors
          if (initialIds.length === 1 && avgTargetCount > 0) {
            // Single supervisor gets full target_count
            setAssignments({
              [initialIds[0]]: { assigned_count: avgTargetCount }
            });
            setDistributeEvenly(false);
          } else if (initialIds.length > 1 && avgTargetCount > 0) {
            // Multiple supervisors - default to even distribution
            const countPerSupervisor = Math.floor(avgTargetCount / initialIds.length);
            const remainder = avgTargetCount % initialIds.length;
            initialIds.forEach((supervisorId, index) => {
              initialAssignments[supervisorId] = {
                assigned_count: countPerSupervisor + (index < remainder ? 1 : 0)
              };
            });
            setAssignments(initialAssignments);
            setDistributeEvenly(true);
          }
        }
        } else {
          // No supervisors selected, clear assignments
          setAssignments({});
          setDistributeEvenly(true);
        }
        
        // Mark initialization as complete after a brief delay to ensure state updates
        setTimeout(() => {
          setIsInitializing(false);
        }, 100);
        
        if (isBulk) {
          // For bulk mode, fetch supervisors from first cell's suggested supervisors
          if (cells.length > 0 && cells[0].id) {
            fetchSuggestedSupervisors(cells[0].id);
          } else {
            // Fallback: fetch all supervisors
            fetchSuggestedSupervisors();
          }
        } else {
          fetchSuggestedSupervisors();
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulk ? cells.map(c => c.id).join(',') : currentCell?.id, isBulk ? undefined : currentCell?.assigned_supervisors, pendingAssignments]);

  // Recalculate distribution when distributeEvenly toggle changes (user action)
  // BUT only if not initializing (to prevent race condition)
  useEffect(() => {
    // Only recalculate when distributeEvenly is explicitly toggled by user
    // Skip during initialization to prevent overwriting prefilled values
    if (!isInitializing && distributeEvenly && selectedSupervisorIds.length > 1 && avgTargetCount > 0) {
      recalculateDistribution();
    }
  }, [distributeEvenly, isInitializing]);

  const recalculateDistribution = () => {
    if (selectedSupervisorIds.length === 0 || avgTargetCount === 0) return;
    
    const countPerSupervisor = Math.floor(avgTargetCount / selectedSupervisorIds.length);
    const remainder = avgTargetCount % selectedSupervisorIds.length;
    
    const newAssignments = {};
    selectedSupervisorIds.forEach((supervisorId, index) => {
      // When recalculating, always use even distribution (don't preserve existing)
      newAssignments[supervisorId] = {
        assigned_count: countPerSupervisor + (index < remainder ? 1 : 0)
      };
    });
    setAssignments(newAssignments);
  };

  const fetchSuggestedSupervisors = async (cellId = null) => {
    try {
      setLoading(true);
      const targetCellId = cellId || currentCell?.id;
      if (!targetCellId) {
        // If no cell ID, fetch all supervisors
        const response = await axios.get('/supervisors');
        setSupervisors(response.data.data || []);
        setSuggestedIds([]);
        return;
      }
      const response = await axios.get(
        `/quotas/projects/${quotaProjectId}/cells/${targetCellId}/suggested-supervisors`
      );
      setSupervisors(response.data.data || []);
      setSuggestedIds(response.data.suggested || []);
    } catch (error) {
      console.error('Error fetching suggested supervisors:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: 'Erreur lors du chargement des prestataires',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSupervisorToggle = (supervisorId) => {
    const isSelected = selectedSupervisorIds.includes(supervisorId);
    
    if (isSelected) {
      // Check if supervisor has completes before allowing removal
      if (!isBulk) {
        const existingCompletes = getExistingCompletes(supervisorId);
        if (existingCompletes > 0) {
          setAlertModal({
            show: true,
            title: 'Action impossible',
            message: `Ce prestataire a déjà complété ${existingCompletes} quota(s). Il ne peut pas être retiré. Vous pouvez seulement réduire son quota assigné jusqu'à ${existingCompletes} (le minimum).`,
            type: 'warning'
          });
          return; // Prevent unselecting
        }
      } else {
        // For bulk mode, check if any cell has completes for this supervisor
        let hasCompletes = false;
        let completesCount = 0;
        cells.forEach(cell => {
          const cellId = Number(cell.id);
          const supervisor = cell?.assigned_supervisors?.find(s => Number(s.id) === supervisorId);
          if (supervisor?.completes !== undefined && Number(supervisor.completes) > 0) {
            hasCompletes = true;
            completesCount = Math.max(completesCount, Number(supervisor.completes));
          }
        });
        
        if (hasCompletes) {
          setAlertModal({
            show: true,
            title: 'Action impossible',
            message: `Ce prestataire a déjà complété ${completesCount} quota(s) dans au moins une cellule. Il ne peut pas être retiré. Vous pouvez seulement réduire son quota assigné jusqu'à ${completesCount} (le minimum).`,
            type: 'warning'
          });
          return; // Prevent unselecting
        }
      }
      
      // Remove supervisor (only if no completes)
      setSelectedSupervisorIds(prev => prev.filter(id => id !== supervisorId));
      setAssignments(prev => {
        const newAssignments = { ...prev };
        delete newAssignments[supervisorId];
        return newAssignments;
      });
    } else {
      // Add supervisor
      const newSelectedIds = [...selectedSupervisorIds, supervisorId];
      setSelectedSupervisorIds(newSelectedIds);
      
      // Check for existing assigned_count (prioritize pending over approved)
      const existingCount = getExistingAssignedCount(supervisorId);
      
      if (distributeEvenly && avgTargetCount > 0 && newSelectedIds.length > 1) {
        // If distributing evenly and multiple supervisors, recalculate
        setTimeout(() => {
          const countPerSupervisor = Math.floor(avgTargetCount / newSelectedIds.length);
          const remainder = avgTargetCount % newSelectedIds.length;
          const newAssignments = {};
          newSelectedIds.forEach((id, index) => {
            // Preserve existing assignments, otherwise calculate
            const existing = isBulk ? null : getExistingAssignedCount(id);
            if (existing !== null && existing !== undefined) {
              newAssignments[id] = { assigned_count: existing };
            } else {
              newAssignments[id] = {
                assigned_count: countPerSupervisor + (index < remainder ? 1 : 0)
              };
            }
          });
          setAssignments(newAssignments);
        }, 0);
      } else {
        // Initialize with existing count or calculate based on single/multiple
        if (existingCount !== null && existingCount !== undefined) {
          // Use existing assignment
          setAssignments(prev => ({
            ...prev,
            [supervisorId]: { assigned_count: existingCount }
          }));
          setDistributeEvenly(false);
        } else if (newSelectedIds.length === 1 && avgTargetCount > 0 && !isBulk) {
          // Single supervisor gets full target_count (only for single cell mode)
          setAssignments({
            [supervisorId]: { assigned_count: avgTargetCount }
          });
          setDistributeEvenly(false);
        } else {
          // Multiple supervisors, no existing assignment, initialize with 0
          setAssignments(prev => ({
            ...prev,
            [supervisorId]: { assigned_count: 0 }
          }));
        }
      }
    }
    
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[supervisorId];
      delete newErrors.total;
      return newErrors;
    });
  };

  const handleAssignmentChange = (supervisorId, value) => {
    const newValue = parseInt(value) || 0;
    
    if (newValue < 0) {
      setErrors(prev => ({
        ...prev,
        [supervisorId]: 'Le nombre ne peut pas être négatif'
      }));
      return;
    }
    
    // Check if new value is less than existing completes (works for both single cell and bulk mode)
    const existingCompletes = getExistingCompletes(supervisorId);
    if (newValue < existingCompletes) {
      setErrors(prev => ({
        ...prev,
        [supervisorId]: `Le nombre assigné (${newValue}) ne peut pas être inférieur au nombre déjà complété (${existingCompletes})`
      }));
      // Still update the value so user can see the error, but validation will prevent submission
    } else {
      // Clear error if validation passes
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[supervisorId];
        return newErrors;
      });
    }
    
    setAssignments(prev => ({
      ...prev,
      [supervisorId]: { assigned_count: newValue }
    }));
    
    // Clear total error when individual assignment changes
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.total;
      return newErrors;
    });
  };

  const validateAssignments = () => {
    const newErrors = {};
    
    if (selectedSupervisorIds.length === 0) {
      newErrors.general = 'Veuillez sélectionner au moins un prestataire';
      setErrors(newErrors);
      return false;
    }
    
    // If distributing evenly, check if the distribution would violate completes constraint
    if (distributeEvenly && selectedSupervisorIds.length > 1) {
      const countPerSupervisor = Math.floor(avgTargetCount / selectedSupervisorIds.length);
      const remainder = avgTargetCount % selectedSupervisorIds.length;
      
      selectedSupervisorIds.forEach((supervisorId, index) => {
        const wouldReceive = countPerSupervisor + (index < remainder ? 1 : 0);
        const existingCompletes = getExistingCompletes(supervisorId);
        
        if (wouldReceive < existingCompletes) {
          newErrors[supervisorId] = `La distribution équitable donnerait ${wouldReceive} quotas, ce qui est inférieur au nombre déjà complété (${existingCompletes}). Veuillez utiliser la distribution personnalisée.`;
        }
      });
    }
    
    // Validate each supervisor's assigned_count (for custom distribution)
    if (!distributeEvenly) {
      selectedSupervisorIds.forEach(supervisorId => {
        const assignedCount = assignments[supervisorId]?.assigned_count;
        const existingCompletes = getExistingCompletes(supervisorId);
        
        // Check if assigned_count is less than completes (works for both single cell and bulk mode)
        if (assignedCount !== undefined && assignedCount !== null && assignedCount < existingCompletes) {
          newErrors[supervisorId] = `Le nombre assigné (${assignedCount}) ne peut pas être inférieur au nombre déjà complété (${existingCompletes})`;
        }
        
        // Check that all supervisors have an assigned_count (for multiple supervisors)
        if (selectedSupervisorIds.length > 1) {
          if (assignedCount === undefined || assignedCount === null) {
            newErrors[supervisorId] = 'Veuillez spécifier le nombre de quotas';
          }
        }
      });
      
      // Check total assigned doesn't exceed target (for multiple supervisors)
      if (selectedSupervisorIds.length > 1) {
        const totalAssigned = selectedSupervisorIds.reduce((sum, supervisorId) => {
          return sum + (assignments[supervisorId]?.assigned_count || 0);
        }, 0);
        
        if (totalAssigned > avgTargetCount) {
          newErrors.total = `Le total assigné (${totalAssigned}) dépasse le quota cible moyen (${avgTargetCount})`;
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAssign = async () => {
    if (!validateAssignments()) {
      return;
    }

    try {
      setAssigning(true);
      
      // Prepare assignment data
      const assignmentData = {
        supervisor_ids: selectedSupervisorIds
      };
      
      if (isBulk) {
        // Bulk assignment
        assignmentData.quota_cell_ids = cellIds;
        
        // If multiple supervisors, include assigned_count data
        if (selectedSupervisorIds.length > 1) {
          if (distributeEvenly) {
            assignmentData.distribute_evenly = true;
          } else {
            assignmentData.distribute_evenly = false;
            assignmentData.assignments = selectedSupervisorIds.map(supervisorId => ({
              supervisor_id: supervisorId,
              assigned_count: assignments[supervisorId]?.assigned_count || 0
            }));
          }
        } else if (selectedSupervisorIds.length === 1) {
          // Single supervisor - use the value from the input
          assignmentData.distribute_evenly = false;
          assignmentData.assignments = [{
            supervisor_id: selectedSupervisorIds[0],
            assigned_count: assignments[selectedSupervisorIds[0]]?.assigned_count || avgTargetCount
          }];
        }
        
        const response = await axios.post(
          `/quotas/projects/${quotaProjectId}/cells/bulk-assign-supervisors`,
          assignmentData
        );
        
        // Handle response
        const responseData = response.data.data;
        if (responseData?.cells && Array.isArray(responseData.cells)) {
          // Update all cells
          responseData.cells.forEach(updatedCell => {
            onSuccess(updatedCell);
          });
        }
        
        // Handle pending assignments if any
        if (responseData?.pending_assignments && Array.isArray(responseData.pending_assignments)) {
          // Trigger parent to refresh pending assignments
          if (onSuccess) {
            onSuccess({ _pendingAssignments: responseData.pending_assignments });
          }
        }
      } else {
        // Single cell assignment
        // If multiple supervisors, include assigned_count data
        if (selectedSupervisorIds.length > 1) {
          if (distributeEvenly) {
            assignmentData.distribute_evenly = true;
          } else {
            assignmentData.distribute_evenly = false;
            assignmentData.assignments = selectedSupervisorIds.map(supervisorId => ({
              supervisor_id: supervisorId,
              assigned_count: assignments[supervisorId]?.assigned_count || 0
            }));
          }
        } else if (selectedSupervisorIds.length === 1) {
          // Single supervisor - use the value from the input
          assignmentData.assignments = [{
            supervisor_id: selectedSupervisorIds[0],
            assigned_count: assignments[selectedSupervisorIds[0]]?.assigned_count || avgTargetCount
          }];
        }
        
        const response = await axios.post(
          `/quotas/projects/${quotaProjectId}/cells/${currentCell.id}/assign-supervisor`,
          assignmentData
        );
        
        // Handle both direct assignment (chef_terrain_superieur) and pending assignment (chef_terrain)
        const responseData = response.data.data;
        const updatedCell = responseData?.cell || responseData;
        
        // If pending assignments were created, include them in the response
        if (responseData?.pending_assignments && Array.isArray(responseData.pending_assignments)) {
          // Create a new array reference to ensure React detects the change
          updatedCell._pendingAssignments = [...responseData.pending_assignments];
        }
        
        // Call onSuccess callback (this will update state in parent)
        onSuccess(updatedCell);
      }
      
      // Close modal after a brief delay to allow state to update
      setTimeout(() => {
        onClose();
      }, 50);
    } catch (error) {
      console.error('Error assigning supervisors:', error);
      setAlertModal({
        show: true,
        title: 'Erreur',
        message: error.response?.data?.message || 'Erreur lors de l\'assignation',
        type: 'error'
      });
    } finally {
      setAssigning(false);
    }
  };

  const isSuggested = (supervisorId) => suggestedIds.includes(supervisorId);

  const totalAssigned = selectedSupervisorIds.reduce((sum, supervisorId) => {
    return sum + (assignments[supervisorId]?.assigned_count || 0);
  }, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...dismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            {isBulk 
              ? `Assigner des prestataires (${cells.length} cellules)`
              : 'Assigner un prestataire'
            }
          </h3>
          <button
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Chargement des prestataires...</p>
            </div>
          ) : (
            <>
              {suggestedIds.length > 0 && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <FontAwesomeIcon icon={faStar} className="mr-1" />
                    {suggestedIds.length} prestataire(s) suggéré(s) basé sur la région/ville de cette cellule
                  </p>
                </div>
              )}

              {/* Quota Division Section */}
              {selectedSupervisorIds.length >= 1 && avgTargetCount > 0 && (
                <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="mb-3">
                    {selectedSupervisorIds.length > 1 && <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={distributeEvenly}
                        onChange={(e) => {
                          const newDistributeEvenly = e.target.checked;
                          setDistributeEvenly(newDistributeEvenly);
                          
                          if (newDistributeEvenly) {
                            // When toggling ON, recalculate evenly
                            recalculateDistribution();
                          } else {
                            // When toggling OFF, restore existing assignments if they exist
                            const restoredAssignments = {};
                            selectedSupervisorIds.forEach(supervisorId => {
                              const existingCount = isBulk ? null : getExistingAssignedCount(supervisorId);
                              if (existingCount !== null && existingCount !== undefined) {
                                restoredAssignments[supervisorId] = { assigned_count: existingCount };
                              } else {
                                // If no existing assignment, keep current or set to 0
                                restoredAssignments[supervisorId] = assignments[supervisorId] || { assigned_count: 0 };
                              }
                            });
                            setAssignments(restoredAssignments);
                          }
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Distribuer équitablement</span>
                    </label>}
                    {selectedSupervisorIds.length > 1 && distributeEvenly && (() => {
                      const countPerSupervisor = Math.floor(avgTargetCount / selectedSupervisorIds.length);
                      const remainder = avgTargetCount % selectedSupervisorIds.length;
                      
                      return (
                        <div className="mt-2 text-xs text-gray-600">
                          {isBulk 
                            ? `Distribution équitable pour chaque cellule: ${selectedSupervisorIds.length} prestataire(s) pour un quota moyen de ${avgTargetCount}.`
                            : `Distribution: ${selectedSupervisorIds.length} prestataire(s) pour ${avgTargetCount} quotas.`
                          }
                          {remainder === 0 ? (
                            ` Chaque prestataire recevra exactement ${countPerSupervisor} quotas${isBulk ? ' par cellule' : ''}.`
                          ) : (
                            ` Les ${remainder} premier(s) prestataire(s) recevront ${countPerSupervisor + 1} quotas${isBulk ? ' par cellule' : ''}, les autres ${countPerSupervisor} quotas${isBulk ? ' par cellule' : ''}.`
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {(selectedSupervisorIds.length === 1 || !distributeEvenly) && (
                    <div className="space-y-3">
                      <div className="text-sm text-gray-600 mb-2">
                        {isBulk
                          ? `Quota moyen par cellule: ${avgTargetCount}`
                          : `Quota cible: ${avgTargetCount}`
                        }
                        {!isBulk && currentCell && (() => {
                          const pendingMod = pendingModifications.find(
                            pm => Number(pm.quota_cell_id) === Number(currentCell.id) && pm.field === 'target_count'
                          );
                          if (pendingMod) {
                            return (
                              <span className="ml-1 text-xs text-yellow-700 bg-yellow-100 px-1.5 py-0.5 rounded">
                                en attente (actuel: {currentCell.target_count})
                              </span>
                            );
                          }
                          return null;
                        })()}
                        {!isBulk && (
                          <>
                            {' | '}Total assigné: <span className="font-semibold">{totalAssigned}</span>
                            {totalAssigned > avgTargetCount && (
                              <span className="ml-2 text-red-600 font-semibold">
                                ⚠ Dépassement de {totalAssigned - avgTargetCount}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      
                      {selectedSupervisorIds.map(supervisorId => {
                        const supervisor = supervisors.find(s => s.id === supervisorId);
                        const assignment = assignments[supervisorId] || { assigned_count: 0 };
                        const existingCompletes = getExistingCompletes(supervisorId);
                        const minValue = existingCompletes > 0 ? existingCompletes : 0;
                        
                        return (
                          <div key={supervisorId} className="p-3 bg-white border border-gray-200 rounded">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900">
                                {supervisor?.name || `Prestataire #${supervisorId}`}
                                {supervisor?.ref && <span className="text-gray-500 ml-1">({supervisor.ref})</span>}
                                {existingCompletes > 0 && (
                                  <span className="text-xs text-gray-500 ml-2">
                                    (Complété: {existingCompletes})
                                  </span>
                                )}
                              </span>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 block mb-1">
                                Nombre de quotas
                                {minValue > 0 && (
                                  <span className="text-red-600 ml-1">(minimum: {minValue})</span>
                                )}
                              </label>
                              <input
                                type="number"
                                min={minValue}
                                max={isBulk ? undefined : avgTargetCount}
                                value={assignment.assigned_count || 0}
                                onChange={(e) => handleAssignmentChange(supervisorId, e.target.value)}
                                className={`w-full px-3 py-2 border rounded ${
                                  errors[supervisorId] ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder={isBulk ? "Quota par cellule" : "Nombre de quotas"}
                              />
                              {errors[supervisorId] && (
                                <div className="text-xs text-red-600 mt-1">{errors[supervisorId]}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              
              {/* Bulk mode info */}
              {isBulk && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Les prestataires sélectionnés seront assignés à {cells.length} cellule(s). 
                    {distributeEvenly 
                      ? ' La distribution sera équitable pour chaque cellule.'
                      : ' Vous pouvez personnaliser la distribution pour chaque cellule.'
                    }
                  </p>
                </div>
              )}

              {/* Supervisors List */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner les prestataires *
                </label>
                {/* Search bar */}
                <div className="flex mb-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setSearchTerm(searchInput.trim().toLowerCase()); }}
                      placeholder="Rechercher par nom, email, ref..."
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-l-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={() => { setSearchInput(''); setSearchTerm(''); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FontAwesomeIcon icon={faTimes} className="text-sm" />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSearchTerm(searchInput.trim().toLowerCase())}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {supervisors.length === 0 ? (
                    <p className="text-center py-8 text-gray-500">Aucun prestataire disponible</p>
                  ) : (
                    <div className="space-y-1 p-2">
                      {supervisors
                        .filter((s) => {
                          if (!searchTerm) return true;
                          return (
                            (s.name && s.name.toLowerCase().includes(searchTerm)) ||
                            (s.email && s.email.toLowerCase().includes(searchTerm)) ||
                            (s.ref && s.ref.toLowerCase().includes(searchTerm)) ||
                            (s.region && s.region.toLowerCase().includes(searchTerm)) ||
                            (s.city_name && s.city_name.toLowerCase().includes(searchTerm))
                          );
                        })
                        .sort((a, b) => {
                          // Sort: suggested supervisors first
                          const aSuggested = isSuggested(a.id);
                          const bSuggested = isSuggested(b.id);
                          if (aSuggested && !bSuggested) return -1;
                          if (!aSuggested && bSuggested) return 1;
                          return 0;
                        })
                        .map((supervisor) => {
                          const isSuggestedSupervisor = isSuggested(supervisor.id);
                          const isSelected = selectedSupervisorIds.includes(supervisor.id);
                          return (
                            <label
                              key={supervisor.id}
                              className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? isSuggestedSupervisor
                                    ? 'border-blue-500 bg-blue-100'
                                    : 'border-blue-500 bg-blue-50'
                                  : isSuggestedSupervisor
                                  ? 'border-green-300 bg-green-50 hover:bg-green-100'
                                  : 'border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                name="supervisor"
                                value={supervisor.id}
                                checked={isSelected}
                                onChange={() => handleSupervisorToggle(supervisor.id)}
                                className="mr-3"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className={`font-medium ${isSuggestedSupervisor ? 'text-green-800' : 'text-gray-900'}`}>
                                    {supervisor.name}
                                  </span>
                                  {isSuggestedSupervisor && (
                                    <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-1 rounded font-medium">
                                      <FontAwesomeIcon icon={faStar} className="mr-1" />
                                      Sugéré
                                    </span>
                                  )}
                                </div>
                                <div className={`text-sm mt-1 ${isSuggestedSupervisor ? 'text-green-700' : 'text-gray-600'}`}>
                                  <span>{supervisor.email}</span>
                                  {supervisor.ref && (
                                    <span className="ml-2">({supervisor.ref})</span>
                                  )}
                                </div>
                                <div className={`text-xs mt-1 ${isSuggestedSupervisor ? 'text-green-600' : 'text-gray-500'}`}>
                                  {supervisor.region && <span>Région: {supervisor.region}</span>}
                                  {supervisor.city_name && (
                                    <span className={supervisor.region ? 'ml-2' : ''}>
                                      Ville: {supervisor.city_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              {/* Error messages */}
              {errors.general && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{errors.general}</p>
                </div>
              )}
              {errors.total && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{errors.total}</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onClose}
            disabled={assigning}
          >
            Annuler
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleAssign}
            disabled={assigning || selectedSupervisorIds.length === 0 || Object.keys(errors).length > 0}
          >
            <FontAwesomeIcon icon={faUserCheck} className="mr-2" />
            {assigning ? 'Assignation...' : `Assigner (${selectedSupervisorIds.length})`}
          </button>
        </div>
      </div>

      <AlertModal
        show={alertModal.show}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
      />
    </div>
  );
};

export default AssignSupervisorModal;
